import { Identified } from "@/types/sdk-types";
import { GridSortItem } from "@mui/x-data-grid";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useRef } from "react";
import {
  GetAllClaim1DateRangeEnum,
  GetAllSuperBillV2BillStatusEnum,
  SuperBillControllerApi,
  SuperBill,
} from "../../services";
import {
  DGT_Pagination,
  getConfigObject,
  getPageIndexWhileSearch,
  getPageSize,
  useBaseContent,
} from "../../utils/utils";

interface Params {
  pagination: DGT_Pagination;
  gridSorting?: GridSortItem[];
  searchKey?: string;
  status?: boolean | undefined;
  xTENANTID?: string;
  encounterId?: string;
  providerId?: string;
  patientId?: string;
  locationId?: string;
  dateRange?: GetAllClaim1DateRangeEnum;
  startDate?: string;
  endDate?: string;
  enabled?: boolean;
  billStatus?: GetAllSuperBillV2BillStatusEnum;
  archive?: boolean;
  /** When true, returns only invoices with an outstanding (unpaid) balance. */
  outstandingBalance?: boolean;
}

/**
 * @author [Atish Bagate]
 */
export function useSuperBillListV2({
  gridSorting,
  pagination,
  searchKey,
  xTENANTID,
  status,
  locationId,
  encounterId,
  providerId,
  patientId,
  enabled,
  billStatus,
  dateRange,
  startDate,
  endDate,
  archive = false,
  outstandingBalance,
}: Params): useBaseContent<Identified<SuperBill>> {
  const prevPage = useRef<number>(pagination.page);

  const pageIndex = getPageIndexWhileSearch({
    pagination,
    searchKey,
    prevPage,
  });
  const pageSize = getPageSize(searchKey, pagination);

  const sortData = gridSorting?.[0];

  return useQuery({
    queryKey: [
      "superbill-List-V2",
      pageSize,
      pageIndex,
      searchKey,
      xTENANTID,
      status,
      sortData,
      locationId,
      encounterId,
      providerId,
      patientId,
      enabled,
      billStatus,
      dateRange,
      startDate,
      endDate,
      archive,
      outstandingBalance,
    ],
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,

    queryFn: async () => {
      return new SuperBillControllerApi(getConfigObject())
        .getAllSuperBillV2(
          pageIndex, //page
          pageSize, //size
          sortData?.field ?? undefined, // sortBy
          sortData?.sort ?? undefined, // sortDirection
          status as boolean, //status
          archive, // archive
          patientId, //patientId,
          locationId, //locationUuid
          encounterId, //encounterId
          providerId, //providerId
          searchKey ? searchKey : undefined, //searchString
          billStatus, //billStatus
          outstandingBalance, //outstandingBalance
          dateRange, // dateRangeType,
          startDate, // startDate,
          endDate, // endDate
          xTENANTID //xTENANTID
        )
        .then((res) => res?.data);
    },
  });
}
// * @param {string} [dateRangeType]
// * @param {string} [customStartDate]
// * @param {string} [customEndDate]
